import { isAutocompleteOption } from '@helpers'
import { FIELD_VALUE_TYPE, OBJECT_FIELD_TYPE } from '@constants'
import { FormRow, ObjectDataRecord, ObjectDataValue } from '@types'

/**
 * Проверяет является ли поле обогащенным (field.objectValue)
 */
const isEnrichedField = (fieldCode: string, enrichedFieldsSet: Set<string>): boolean => {
  return enrichedFieldsSet.has(fieldCode)
}

/**
 * Проверяет является ли поле ссылочным (требует сохранения label)
 */
const isLinkField = (field?: FormRow): boolean => {
  return Boolean(
    field &&
      (field.valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK ||
        field.valueType === FIELD_VALUE_TYPE.OBJ_PK_LINK)
  )
}

/**
 * Обновляет значение поля-ссылки, сохраняя label
 */
const updateLinkField = (
  currentValue: ObjectDataValue,
  putValue: ObjectDataValue
): ObjectDataValue => {
  if (isAutocompleteOption(currentValue)) {
    return { ...currentValue, id: putValue as string | number }
  }

  return putValue
}

// Аналог updateLinkField для array полей — берём label из текущей формы или из enriched map
const updateArrayLinkField = (
  currentValue: ObjectDataValue,
  putValue: ObjectDataValue,
  enrichedNames?: ObjectDataValue
): ObjectDataValue => {
  if (!Array.isArray(putValue)) return putValue

  const currentArray = Array.isArray(currentValue) ? currentValue : []
  const namesMap =
    enrichedNames && typeof enrichedNames === 'object' && !Array.isArray(enrichedNames)
      ? (enrichedNames as Record<string, string>)
      : undefined

  return putValue.map(putItem => {
    if (isAutocompleteOption(putItem)) return putItem

    const existing = currentArray.find(
      cur => isAutocompleteOption(cur) && String(cur.id) === String(putItem)
    )
    if (existing) return existing

    if (namesMap && namesMap[putItem as string]) {
      return { id: putItem, label: namesMap[putItem as string] }
    }

    return { id: putItem, label: String(putItem) }
  })
}

/**
 * Создает Set обогащенных полей на основе конфигурации формы
 */
const buildEnrichedFieldsSet = (fields: FormRow[]): Set<string> => {
  const enrichedFields = new Set<string>()

  fields.forEach(field => {
    if (field.objectValue && isLinkField(field)) {
      enrichedFields.add(`${field.code}.${field.objectValue}`)
    }
  })

  return enrichedFields
}

/**
 * Объединяет текущие данные формы с данными из PUT ответа.
 *
 * Для полей типа OBJ_INTERNAL_ID_LINK и OBJ_PK_LINK сохраняет структуру { id, label },
 * чтобы не потерять обогащенные данные (label значения).
 *
 * @param currentFormData - текущие данные формы (с обогащенными полями)
 * @param putResponseData - данные из ответа PUT запроса
 * @param fields - конфигурация полей формы
 * @returns объединенные данные для сброса формы
 */
export const mergeFormDataAfterUpdate = (
  currentFormData: ObjectDataRecord,
  putResponseData: ObjectDataRecord,
  fields: FormRow[]
): ObjectDataRecord => {
  const enrichedFieldsSet = buildEnrichedFieldsSet(fields)
  const fieldsMap = new Map(fields.map(f => [f.code, f]))

  // Копируем все текущие данные формы
  const mergedData: ObjectDataRecord = { ...currentFormData }

  // Обновляем поля из PUT ответа
  Object.entries(putResponseData).forEach(([fieldCode, putValue]) => {
    // Пропускаем обогащенные поля - они должны остаться из currentFormData
    if (isEnrichedField(fieldCode, enrichedFieldsSet)) {
      return
    }

    const field = fieldsMap.get(fieldCode)

    // null от сервера для array поля = нет данных (как при первом открытии формы)
    if (putValue === null && field?.type === OBJECT_FIELD_TYPE.ARRAY) {
      mergedData[fieldCode] = currentFormData[fieldCode] ?? []

      return
    }

    // Для полей-ссылок сохраняем label
    if (isLinkField(field)) {
      if (Array.isArray(putValue)) {
        const enrichedKey = field?.objectValue ? `${fieldCode}.${field.objectValue}` : ''
        const enrichedNames = enrichedKey ? putResponseData[enrichedKey] : undefined
        mergedData[fieldCode] = updateArrayLinkField(
          currentFormData[fieldCode],
          putValue,
          enrichedNames
        )
      } else {
        mergedData[fieldCode] = updateLinkField(currentFormData[fieldCode], putValue)
      }
    } else {
      // Для обычных полей просто обновляем значение
      mergedData[fieldCode] = putValue
    }
  })

  return mergedData
}
